/**
 * Timeline component adapted from craftedbygc/2018-in-review
 * https://github.com/craftedbygc/2018-in-review
 *
 * Uses 2D HTML text with 3D scroll effect
 * Classic 1980s retro font styling
 * Fonts (VT323, Press Start 2P) preloaded in index.html
 */
import React, { useEffect, useRef, useState } from 'react';

// Timeline sections data - adapted for your career
const sections = [
  {
    id: 'intro',
    name: 'ABOUT ME',
    year: '',
    subtitle: 'My Journey',
    color: 'var(--timeline-intro-color, #ffffff)',
    terminal: {
      lines: [
        '$ whoami',
        '> Curiosity-driven engineer',
        '> PhD in control systems & optimization',
        '> From underground mines to cloud infrastructure',
        '$ interests --list',
        '> Photography, animations, film editing',
        '> Hiking, meditation, travel',
        '> Hardware circuits & software systems',
        '> Finding restaurants with food you\'ve never heard of',
        '$ philosophy',
        '> "Every constraint forces creativity"',
        '> Experiment, iterate, break through limitations',
      ]
    }
  },
  {
    id: 'sec2024',
    name: 'CLOUD & AI',
    year: '2024-NOW',
    subtitle: 'AI Systems Engineer',
    color: '#3b82f6',
    terminal: {
      lines: [
        '$ stack --current',
        '> AWS: Lambda, API Gateway, DynamoDB, S3, CDK',
        '> Frontend: React, TypeScript, Next.js',
        '> AI/ML: OpenAI, Anthropic, Google LLMs',
        '> Backend: Python, Node.js, Serverless',
        '$ projects --highlight',
        '> Multi-tenant IoT platform (device monitoring)',
        '> AI chatbot with vision, voice, tool calling',
        '> RAG systems for regulatory compliance',
        '> Real-time voice AI with sub-second latency',
        '$ approach',
        '> Framework-first AI-assisted development',
        '> 5-10x faster delivery than traditional methods',
      ]
    }
  },
  {
    id: 'sec2020',
    name: 'EMBEDDED',
    year: '2020-2024',
    subtitle: 'Safety-Critical Systems',
    color: '#f59e0b',
    terminal: {
      lines: [
        '$ domain --list',
        '> Explosion-proof battery management systems',
        '> Underground mining energy systems',
        '> Real-time embedded software',
        '$ standards',
        '> ISO 26262 functional safety',
        '> AUTOSAR architecture',
        '$ lesson',
        '> Architecture design where mistakes matter',
        '> Memory constraints teach efficiency',
        '> Every design decision has consequences',
      ]
    }
  },
  {
    id: 'sec2017',
    name: 'RESEARCH',
    year: '2017-2020',
    subtitle: 'Research Fellow',
    color: '#10b981',
    terminal: {
      lines: [
        '$ publications --list',
        '> 8 peer-reviewed papers',
        '> Applied Energy, Journal of Power Sources, Energy',
        '> IEEE conferences (GCCE, IECON, ICCVE)',
        '> CNN+Transformer for battery SOH estimation',
        '$ focus --areas',
        '> SOC estimation for battery systems',
        '> Kalman filtering algorithms',
        '> Cloud-based IoT monitoring platforms',
        '> Underground electric mining vehicles',
        '$ affiliation',
        '> University of Wollongong, Australia',
      ]
    }
  },
  {
    id: 'sec2013',
    name: 'PHD',
    year: '2013-2017',
    subtitle: 'Battery Management Systems',
    color: '#8b5cf6',
    terminal: {
      lines: [
        '$ thesis --title',
        '> "Battery Management System for Electric Vehicles"',
        '$ research --methods',
        '> SOC estimation via Kalman filtering',
        '> State estimation under uncertainty',
        '> Particle Swarm Optimization (PSO)',
        '$ skills --developed',
        '> Control system theory',
        '> Mathematical optimization',
        '> Real-time signal processing',
        '> Safety-critical system design',
      ]
    }
  },
  {
    id: 'sec2008',
    name: 'BACHELOR',
    year: '2008-2012',
    subtitle: 'Electrical & Electronics Engineering',
    color: '#ef4444',
    terminal: {
      lines: [
        '$ degree --info',
        '> B.Eng (Honours) Electrical & Electronics',
        '> First Class Honours',
        '> Universiti Tenaga Nasional, Malaysia',
        '$ capstone --project',
        '> PSO for traffic signal control',
        '> Multi-objective optimization under constraints',
        '$ foundation',
        '> Circuit design & power systems',
        '> Digital signal processing',
        '> Embedded systems fundamentals',
      ]
    }
  }
];

const TimelineCrafted = () => {
  const containerRef = useRef(null);
  const scrollPosRef = useRef(0);
  const scrollVelocityRef = useRef(0);
  const animationIdRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [sectionTransforms, setSectionTransforms] = useState(
    sections.map((_, i) => ({ z: i * 1000, opacity: i === 0 ? 1 : 0.3, scale: 1, cardZ: i * 1000 + 1000, cardOpacity: 0, cardScale: 1 }))
  );
  const [typedTexts, setTypedTexts] = useState({});
  const [visibleTerminals, setVisibleTerminals] = useState({});
  const typingRefs = useRef({});
  const [loopArrowOpacity, setLoopArrowOpacity] = useState(0);
  const [loopArrowScale, setLoopArrowScale] = useState(0.3);
  const [isRewinding, setIsRewinding] = useState(false);

  // Detect mobile - check on initial render too
  const getIsMobile = () => typeof window !== 'undefined' && window.innerWidth <= 768;
  const [isMobile, setIsMobile] = useState(getIsMobile);

  // Update on resize
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Rewind animation - zoom backward to ABOUT ME
  const handleRewind = () => {
    if (isRewinding) return;
    setIsRewinding(true);

    const rewindAnimation = () => {
      if (scrollPosRef.current > 0) {
        scrollPosRef.current -= 150; // Fast rewind speed
        if (scrollPosRef.current < 0) scrollPosRef.current = 0;
        requestAnimationFrame(rewindAnimation);
      } else {
        setIsRewinding(false);
      }
    };
    requestAnimationFrame(rewindAnimation);
  };

  // Track which terminals have started typing (separate from completion)
  const typingStartedRef = useRef({});

  // Typing effect for each terminal
  useEffect(() => {
    sections.forEach((section) => {
      if (!section.terminal) return;
      if (!visibleTerminals[section.id]) return;
      if (typingStartedRef.current[section.id]) return; // Already started typing

      // Mark as started
      typingStartedRef.current[section.id] = true;

      const fullText = section.terminal.lines.join('\n');
      let charIndex = 0;

      setTypedTexts(prev => ({ ...prev, [section.id]: '' }));

      const typeChar = () => {
        if (charIndex <= fullText.length) {
          setTypedTexts(prev => ({ ...prev, [section.id]: fullText.slice(0, charIndex) }));
          charIndex++;
          typingRefs.current[section.id] = setTimeout(typeChar, 25);
        }
      };

      typeChar();
    });
    // No cleanup - let typing animations complete
  }, [visibleTerminals]);

  useEffect(() => {
    if (!containerRef.current) return;

    const container = containerRef.current;
    const sectionSpacing = 2000;
    const totalScroll = (sections.length - 1) * sectionSpacing + 2000; // +2000 for last terminal + loop arrow

    // Scroll handler with momentum
    const handleWheel = (e) => {
      e.preventDefault();
      const delta = e.deltaY;
      scrollVelocityRef.current += delta * 0.5;
    };

    // Touch handlers for mobile - with momentum tracking
    let lastTouchY = 0;
    let lastTouchTime = 0;
    let touchActive = false;
    let touchVelocity = 0;

    const handleTouchStart = (e) => {
      touchActive = true;
      lastTouchY = e.touches[0].clientY;
      lastTouchTime = Date.now();
      touchVelocity = 0;
      // Stop any existing momentum when touch starts
      scrollVelocityRef.current = 0;
    };

    const handleTouchMove = (e) => {
      if (!touchActive) return;
      e.preventDefault();
      e.stopPropagation();

      const touchY = e.touches[0].clientY;
      const now = Date.now();
      const timeDelta = now - lastTouchTime;
      const delta = lastTouchY - touchY;

      // Calculate velocity (pixels per millisecond) for momentum
      if (timeDelta > 0) {
        touchVelocity = (delta / timeDelta) * 16; // Scale to ~60fps frame time
      }

      // Direct scroll position update for immediate response
      const newPos = scrollPosRef.current + delta * 2;

      // Soft boundary with resistance instead of hard clamp
      if (newPos < 0) {
        scrollPosRef.current = newPos * 0.3; // Resistance at top
      } else if (newPos > totalScroll) {
        scrollPosRef.current = totalScroll + (newPos - totalScroll) * 0.3; // Resistance at bottom
      } else {
        scrollPosRef.current = newPos;
      }

      lastTouchY = touchY;
      lastTouchTime = now;
    };

    const handleTouchEnd = () => {
      touchActive = false;

      // Apply momentum from swipe velocity
      if (Math.abs(touchVelocity) > 0.5) {
        scrollVelocityRef.current = touchVelocity * 8; // Momentum multiplier
      }

      // Snap back if overscrolled
      if (scrollPosRef.current < 0) {
        scrollVelocityRef.current = -scrollPosRef.current * 0.2;
      } else if (scrollPosRef.current > totalScroll) {
        scrollVelocityRef.current = -(scrollPosRef.current - totalScroll) * 0.2;
      }
    };

    // Animation loop
    const animate = () => {
      animationIdRef.current = requestAnimationFrame(animate);

      // Apply velocity to scroll position with friction
      scrollPosRef.current += scrollVelocityRef.current;
      scrollVelocityRef.current *= 0.92; // Friction

      // Soft clamp scroll position with snap-back for overscroll
      if (scrollPosRef.current < 0) {
        // Snap back to 0 with spring effect
        scrollPosRef.current *= 0.85;
        if (Math.abs(scrollPosRef.current) < 1) {
          scrollPosRef.current = 0;
          scrollVelocityRef.current = 0;
        }
      }
      if (scrollPosRef.current > totalScroll) {
        // Snap back to max with spring effect
        const overscroll = scrollPosRef.current - totalScroll;
        scrollPosRef.current = totalScroll + overscroll * 0.85;
        if (Math.abs(overscroll) < 1) {
          scrollPosRef.current = totalScroll;
          scrollVelocityRef.current = 0;
        }
      }


      // Calculate which section is active
      const newActiveIndex = Math.round(scrollPosRef.current / sectionSpacing);
      if (newActiveIndex !== activeIndex && newActiveIndex >= 0 && newActiveIndex < sections.length) {
        setActiveIndex(newActiveIndex);
      }

      // Calculate transforms for each section
      const newTransforms = sections.map((_, i) => {
        const sectionPos = i * sectionSpacing;
        const distanceFromCamera = sectionPos - scrollPosRef.current;
        const absDistance = Math.abs(distanceFromCamera);
        const scale = Math.max(0.3, 1 - absDistance / 2000);

        // Title - fade but keep visible longer, let it zoom through
        const opacity = Math.max(0, 1 - absDistance / 700);

        // Cards layer - positioned 1000 units behind title, 1000 units before next title
        const cardPos = sectionPos + 1000;
        const cardDistanceFromCamera = cardPos - scrollPosRef.current;
        const cardAbsDistance = Math.abs(cardDistanceFromCamera);
        const cardOpacity = Math.max(0, 1 - cardAbsDistance / 700);
        const cardScale = Math.max(0.6, 1 - cardAbsDistance / 1200);

        return {
          z: distanceFromCamera,
          scale,
          opacity,
          blur: 0,
          cardZ: cardDistanceFromCamera,
          cardOpacity,
          cardScale
        };
      });

      setSectionTransforms(newTransforms);

      // Check which terminals are visible and trigger typing
      newTransforms.forEach((transform, index) => {
        const section = sections[index];
        if (section.terminal && transform.cardOpacity > 0.5) {
          setVisibleTerminals(prev => {
            if (!prev[section.id]) {
              return { ...prev, [section.id]: true };
            }
            return prev;
          });
        }
      });

      // Calculate loop arrow transform (zoom in like other sections, but stop at center)
      const loopArrowPos = (sections.length - 1) * sectionSpacing + 1800;
      const loopArrowDistance = loopArrowPos - scrollPosRef.current;

      // Opacity: fade in as we approach
      const newLoopArrowOpacity = Math.max(0, Math.min(1, 1 - loopArrowDistance / 600));
      setLoopArrowOpacity(newLoopArrowOpacity);

      // Scale: zoom in from far (0.3) to full (1), but cap at 1 (don't zoom past)
      const newLoopArrowScale = Math.min(1, Math.max(0.3, 1 - loopArrowDistance / 1500));
      setLoopArrowScale(newLoopArrowScale);
    };

    container.addEventListener('wheel', handleWheel, { passive: false });
    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd, { passive: true });
    animate();

    return () => {
      container.removeEventListener('wheel', handleWheel);
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
      cancelAnimationFrame(animationIdRef.current);
    };
  }, [activeIndex]);

  return (
    <div className="timeline-crafted-container" ref={containerRef}>
      <div className="timeline-perspective">
        {sections.map((section, index) => {
          const transform = sectionTransforms[index] || { z: 0, scale: 1, opacity: 1, blur: 0 };
          const isActive = index === activeIndex;

          // Mobile: use scale only, Desktop: use translateZ
          const titleTransform = isMobile
            ? `translate(-50%, -50%) scale(${transform.scale || 1})`
            : `translate(-50%, -50%) translateZ(${-transform.z}px) scale(${transform.scale || 1})`;
          const cardTransform = isMobile
            ? `translate(-50%, -50%) scale(${transform.cardScale})`
            : `translate(-50%, -50%) translateZ(${-transform.cardZ}px) scale(${transform.cardScale})`;

          // On mobile, only render visible elements (opacity > 0.1) to prevent stacking issues
          const shouldRenderTitle = !isMobile || transform.opacity > 0.1;
          const shouldRenderCard = !isMobile || transform.cardOpacity > 0.1;

          return (
            <React.Fragment key={section.id}>
              {/* Title layer - on mobile, only render if visible */}
              {shouldRenderTitle && (
                <div
                  className={`timeline-section ${isActive ? 'active' : ''}`}
                  style={{
                    transform: titleTransform,
                    opacity: transform.opacity,
                    color: section.color,
                    zIndex: isMobile ? (isActive ? 10 : 1) : 'auto'
                  }}
                >
                  <h1 className="section-name">{section.name}</h1>
                  <p className="section-year">{section.year}</p>
                  <p className="section-subtitle">{section.subtitle}</p>
                </div>
              )}

              {/* Terminal layer - for intro section */}
              {section.terminal && shouldRenderCard && (
                <div
                  className="timeline-cards-layer"
                  style={{
                    transform: cardTransform,
                    opacity: transform.cardOpacity,
                    zIndex: isMobile ? 5 : 'auto'
                  }}
                >
                  <div className="terminal-card">
                    <div className="terminal-header">
                      <span className="terminal-dot red"></span>
                      <span className="terminal-dot yellow"></span>
                      <span className="terminal-dot green"></span>
                      <span className="terminal-title">kai@limkc ~ </span>
                    </div>
                    <div className="terminal-body">
                      {(typedTexts[section.id] || '').split('\n').map((line, lineIndex) => (
                        <div
                          key={lineIndex}
                          className={`terminal-line ${line.startsWith('$') ? 'command' : 'response'}`}
                        >
                          {line}
                        </div>
                      ))}
                      <span className="terminal-cursor">_</span>
                    </div>
                  </div>
                </div>
              )}

            </React.Fragment>
          );
        })}
      </div>

      {/* Loop back arrow */}
      <div
        className="loop-arrow"
        style={{
          opacity: loopArrowOpacity,
          transform: `translate(-50%, -50%) scale(${loopArrowScale})`,
          pointerEvents: loopArrowOpacity > 0.5 ? 'auto' : 'none'
        }}
        onClick={handleRewind}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
          <path d="M3 3v5h5" />
        </svg>
        <span>Back to start</span>
      </div>

      <div className="timeline-dots">
        {sections.map((section, index) => (
          <div
            key={section.id}
            className={`timeline-dot ${index === activeIndex ? 'active' : ''}`}
            style={{ backgroundColor: index === activeIndex ? section.color : 'rgba(255,255,255,0.3)' }}
            onClick={() => {
              scrollPosRef.current = index * 2000;
              scrollVelocityRef.current = 0;
            }}
          />
        ))}
      </div>

      <style>{`
        .timeline-crafted-container {
          width: 100%;
          height: 100%;
          position: relative;
          overflow: hidden;
          background: var(--bg-primary, #0a0a0f);
          perspective: 1000px;
          perspective-origin: center center;
          --timeline-intro-color: #ffffff;
          /* Prevent iOS rubber-band bounce */
          touch-action: none;
          overscroll-behavior: none;
          -webkit-overflow-scrolling: auto;
        }

        [data-theme="light"] .timeline-crafted-container,
        .light .timeline-crafted-container {
          --timeline-intro-color: #000000;
        }

        @media (prefers-color-scheme: light) {
          .timeline-crafted-container:not([data-theme="dark"]) {
            --timeline-intro-color: #000000;
          }
        }

        .timeline-perspective {
          width: 100%;
          height: 100%;
          position: relative;
          transform-style: preserve-3d;
        }

        .timeline-section {
          position: absolute;
          top: 50%;
          left: 50%;
          transform-origin: center center;
          text-align: center;
          pointer-events: none;
          will-change: transform, opacity;
          transition: color 0.3s ease;
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .timeline-cards-layer {
          position: absolute;
          top: 50%;
          left: 50%;
          pointer-events: none;
          will-change: transform, opacity;
        }

        .floating-card-wrapper {
          position: absolute;
          z-index: 1;
        }

        .floating-card {
          background: rgba(15, 15, 20, 0.9);
          border: 1px solid;
          border-radius: 12px;
          padding: 16px 20px;
          min-width: 140px;
          backdrop-filter: blur(10px);
          box-shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
          display: flex;
          flex-direction: column;
          gap: 6px;
          transform-style: preserve-3d;
          animation: floatCard 4s ease-in-out infinite;
        }

        @keyframes floatCard {
          0%, 100% { margin-top: 0; }
          50% { margin-top: -15px; }
        }

        /* Terminal styles */
        .terminal-card {
          background: rgba(10, 10, 15, 0.95);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 12px;
          width: 700px;
          overflow: hidden;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
        }

        .terminal-header {
          background: rgba(30, 30, 35, 0.9);
          padding: 12px 16px;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .terminal-dot {
          width: 12px;
          height: 12px;
          border-radius: 50%;
        }

        .terminal-dot.red { background: #ff5f56; }
        .terminal-dot.yellow { background: #ffbd2e; }
        .terminal-dot.green { background: #27ca40; }

        .terminal-title {
          margin-left: 12px;
          font-family: 'VT323', monospace;
          font-size: 1rem;
          color: rgba(255, 255, 255, 0.6);
        }

        .terminal-body {
          padding: 20px 24px;
          font-family: 'VT323', monospace;
          font-size: 1.3rem;
          line-height: 1.8;
        }

        .terminal-line {
          color: #22c55e;
        }

        .terminal-line.command {
          color: #3b82f6;
        }

        .terminal-line.response {
          color: #22c55e;
        }

        .terminal-cursor {
          color: #22c55e;
          animation: blink 1s infinite;
        }

        @keyframes blink {
          0%, 50% { opacity: 1; }
          51%, 100% { opacity: 0; }
        }

        @media (max-width: 768px) {
          .terminal-card {
            width: 90vw;
          }

          .terminal-body {
            font-size: 1.1rem;
            padding: 16px;
          }
        }

        .card-title {
          font-family: 'VT323', monospace;
          font-size: 1.4rem;
          font-weight: 400;
        }

        .card-desc {
          font-family: 'VT323', monospace;
          font-size: 1.05rem;
          color: rgba(255, 255, 255, 0.6);
        }

        .section-name {
          position: relative;
          z-index: 10;
          font-family: 'Press Start 2P', 'VT323', monospace;
          font-size: clamp(2rem, 6vw, 5rem);
          font-weight: 400;
          letter-spacing: 0.05em;
          margin: 0;
          white-space: nowrap;
          text-shadow: 0 0 20px currentColor, 0 0 40px currentColor;
        }

        [data-theme="light"] .section-name,
        .light .section-name {
          text-shadow: 0 0 8px currentColor;
        }

        @media (prefers-color-scheme: light) {
          .timeline-crafted-container:not([data-theme="dark"]) .section-name {
            text-shadow: 0 0 8px currentColor;
          }
        }

        .section-year {
          position: relative;
          z-index: 10;
          font-family: 'VT323', monospace;
          font-size: clamp(1.5rem, 4vw, 3rem);
          font-weight: 400;
          opacity: 0.8;
          margin: 1rem 0 0.5rem;
          letter-spacing: 0.1em;
        }

        .section-subtitle {
          position: relative;
          z-index: 10;
          font-family: 'VT323', monospace;
          font-size: clamp(1rem, 2.5vw, 1.75rem);
          font-weight: 400;
          opacity: 0.6;
          margin: 0;
          letter-spacing: 0.05em;
        }

        .loop-arrow {
          position: absolute;
          top: 50%;
          left: 50%;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1rem;
          cursor: pointer;
          z-index: 20;
          overflow: visible;
          will-change: transform, opacity;
        }

        .loop-arrow:hover svg {
          filter: drop-shadow(0 0 20px currentColor);
        }

        .loop-arrow svg {
          width: 80px;
          height: 80px;
          color: var(--timeline-intro-color, #ffffff);
          filter: drop-shadow(0 0 10px currentColor);
          overflow: visible;
        }

        .loop-arrow span {
          font-family: 'VT323', monospace;
          font-size: 1.5rem;
          color: var(--timeline-intro-color, #ffffff);
          text-shadow: 0 0 10px currentColor;
        }

        .timeline-dots {
          position: absolute;
          right: 2rem;
          top: 50%;
          transform: translateY(-50%);
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
          z-index: 10;
        }

        .timeline-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .timeline-dot:hover {
          transform: scale(1.3);
        }

        .timeline-dot.active {
          transform: scale(1.5);
        }

        @media (max-width: 768px) {
          .timeline-crafted-container {
            perspective: none;
          }

          .timeline-perspective {
            transform-style: flat;
          }

          .timeline-dot {
            width: 6px;
            height: 6px;
          }

          .timeline-dots {
            gap: 0.4rem;
          }

          .terminal-card {
            width: 90vw;
          }

          .terminal-body {
            font-size: 1.1rem;
            padding: 16px;
          }

          .loop-arrow svg {
            width: 60px;
            height: 60px;
          }

          .loop-arrow span {
            font-size: 1.2rem;
          }
        }

        /* Mobile portrait - dots at bottom in horizontal row, above footer */
        @media (max-width: 768px) and (orientation: portrait) {
          .timeline-dots {
            top: auto;
            bottom: 4rem;
            right: 50%;
            transform: translateX(50%);
            flex-direction: row;
          }
        }
      `}</style>
    </div>
  );
};

export default TimelineCrafted;
